import { useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import { getI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  MessageCircle, X, Minus, Search, Send, Paperclip, Download,
  ChevronLeft, FileText, Image as ImageIcon,
} from 'lucide-react';

const fmtTime = (iso, lang = 'ru') => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const locale = lang === 'en' ? 'en-US' : 'ru-RU';
    return d.toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const fmtSize = (b, lang = 'ru') => {
  if (b == null) return '';
  if (lang === 'en') {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  }
  if (b < 1024) return `${b} Б`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} КБ`;
  return `${(b / 1024 / 1024).toFixed(2)} МБ`;
};

export default function ChatWidget() {
  const { user } = useAuth();
  const { t, lang } = getI18n(user);
  const { open, activeKey, setActiveKey, threads, refreshThreads, closeWidget, subscribe } = useChat();

  const [minimized, setMinimized] = useState(false);
  const [search, setSearch] = useState('');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragNodeRef = useRef(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  const myEmail = (user?.email || '').toLowerCase();

  const filteredThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter(t =>
      (t.supplier_company || '').toLowerCase().includes(q) ||
      (t.organizer_company || '').toLowerCase().includes(q) ||
      (t.tender_lot_name || '').toLowerCase().includes(q) ||
      String(t.tender_lot_number || '').includes(q) ||
      (t.supplier_email || '').toLowerCase().includes(q)
    );
  }, [threads, search]);

  const active = useMemo(() => {
    if (!activeKey) return null;
    const [tid, se] = activeKey.split(':');
    return threads.find(t => t.tender_id === tid && t.supplier_email === se) || null;
  }, [activeKey, threads]);

  // Загрузка истории при смене активного диалога
  useEffect(() => {
    let cancelled = false;
    if (!active) { setMessages([]); return; }
    setLoadingMsgs(true);
    (async () => {
      try {
        const { data } = await api.get(`/chat/threads/${active.tender_id}/${encodeURIComponent(active.supplier_email)}/messages`);
        if (!cancelled) setMessages(data || []);
        // Помечаем прочитанными
        await api.post(`/chat/threads/${active.tender_id}/${encodeURIComponent(active.supplier_email)}/read`).catch(() => {});
        refreshThreads();
      } catch {} finally {
        if (!cancelled) setLoadingMsgs(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  // Авто-скролл вниз при новых сообщениях
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, active?.tender_id]);

  // Live-подписка: при WS-сообщении сразу добавляем msg в открытый диалог.
  useEffect(() => {
    if (!active) return undefined;
    const unsub = subscribe((evt) => {
      if (!evt || (evt.type !== 'chat:new_message' && evt.type !== 'chat:message_sent')) return;
      if (evt.tender_id !== active.tender_id) return;
      if ((evt.supplier_email || '').toLowerCase() !== active.supplier_email) return;
      const msg = evt.message;
      if (!msg || !msg.id) return;
      setMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev; // дедуп
        return [...prev, msg];
      });
      // Если получили чужое сообщение в открытом диалоге — сразу пометим прочитанным.
      if (evt.type === 'chat:new_message') {
        api.post(`/chat/threads/${active.tender_id}/${encodeURIComponent(active.supplier_email)}/read`).catch(() => {});
      }
    });
    return unsub;
  }, [active, subscribe]);

  // Мобильная разметка — определяется по ширине окна. Должно быть до early-return.
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!user || !open) return null;

  const handleSend = async () => {
    if (!active) return;
    const text = draft.trim();
    if (!text && files.length === 0) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('message_text', text);
      files.forEach(f => fd.append('files', f));
      const { data } = await api.post(
        `/chat/threads/${active.tender_id}/${encodeURIComponent(active.supplier_email)}/messages`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setMessages(m => [...m, data]);
      setDraft('');
      setFiles([]);
      refreshThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('chat.error_send'));
    } finally {
      setSending(false);
    }
  };

  const handleFiles = (list) => {
    const arr = Array.from(list || []);
    const valid = arr.filter(f => f.size <= 10 * 1024 * 1024);
    if (valid.length < arr.length) toast.warning(t('chat.warn_too_large'));
    setFiles(prev => [...prev, ...valid]);
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await api.get(`/chat/attachments/${att.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error(t('chat.error_download'));
    }
  };

  const isImage = (mime) => (mime || '').startsWith('image/');

  // Мобильная разметка: < md → полноэкранный модал, без Draggable.
  // На десктопе — старый плавающий draggable widget.
  // ВАЖНО: эти hooks должны идти до early-return (см. ниже if !open).

  const totalUnread = threads.reduce((s, x) => s + (x.unread_count || 0), 0);
  // Имя «собеседника» для шапки на мобильном (зависит от роли пользователя)
  const peerNameForActive = active
    ? (user.role === 'organizer'
        ? (active.supplier_company || active.supplier_email)
        : (active.organizer_company || t('chat.organizer_default')))
    : '';

  // Внутренняя часть (список диалогов + сообщения + composer).
  const renderInner = () => (
    <div className="flex-1 flex min-h-0">
      {/* Левая колонка — список диалогов. На мобильном видна только если нет активного. */}
      <div className={`border-r border-slate-200 flex flex-col bg-slate-50/40
        ${isMobile ? (active ? 'hidden' : 'flex-1') : 'w-60'}`}>
        <div className="p-2 border-b border-slate-200">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t('chat.search')} className="pl-7 h-8 text-xs rounded-sm border-slate-300"
              data-testid="chat-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="chat-threads-list">
          {filteredThreads.length === 0 ? (
            <div className="p-3 text-[11px] text-slate-400 text-center">{t('chat.empty_threads')}</div>
          ) : filteredThreads.map(th => {
            const key = `${th.tender_id}:${th.supplier_email}`;
            const isActive = key === activeKey;
            const partner = user.role === 'organizer'
              ? th.supplier_company
              : (th.organizer_company || t('chat.organizer_default'));
            const lotLabel = th.tender_lot_number ? `${t('chat.lot_label')}${th.tender_lot_number}` : (th.tender_lot_name || '');
            return (
              <button key={key} onClick={() => setActiveKey(key)}
                data-testid={`chat-thread-${th.tender_id}`}
                className={`w-full text-left px-2.5 py-2.5 border-b border-slate-100 hover:bg-blue-50/50 transition ${isActive ? 'bg-blue-100/70' : ''}`}>
                <div className="flex items-start justify-between gap-1">
                  <div className="font-semibold text-xs text-slate-800 truncate">{partner}</div>
                  {th.unread_count > 0 && (
                    <span className="shrink-0 inline-flex items-center justify-center min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[9px] font-bold rounded-full"
                      data-testid={`chat-unread-${th.tender_id}`}>
                      {th.unread_count}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 truncate">{lotLabel}</div>
                {th.last_message && (
                  <div className="text-[11px] text-slate-600 truncate mt-0.5">
                    {th.last_message.has_attachments && '📎 '}{th.last_message.text || (th.last_message.has_attachments ? t('chat.attachment') : '')}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Правая колонка — переписка. На мобильном видна только если выбран активный. */}
      <div className={`flex flex-col min-w-0 ${isMobile ? (active ? 'flex-1' : 'hidden') : 'flex-1'}`}>
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-xs">
            {t('chat.choose_thread')}
          </div>
        ) : (
          <>
            {/* Заголовок переписки — только на десктопе (на мобильном инфо в header) */}
            {!isMobile && (
              <div className="px-3 py-2 border-b border-slate-200 bg-white flex items-center gap-2">
                <button onClick={() => setActiveKey(null)}
                  className="md:hidden w-7 h-7 rounded hover:bg-slate-100 flex items-center justify-center">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-slate-800 truncate" data-testid="chat-active-partner">
                    {user.role === 'organizer' ? active.supplier_company : (active.organizer_company || t('chat.organizer_default'))}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {active.tender_lot_number ? `${t('chat.lot_label')}${active.tender_lot_number} · ` : ''}{active.tender_lot_name}
                  </div>
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto bg-slate-50/40 p-3 space-y-2" data-testid="chat-messages">
              {loadingMsgs ? (
                <div className="text-[11px] text-center text-slate-400 py-2">{t('chat.loading')}</div>
              ) : messages.length === 0 ? (
                <div className="text-[11px] text-center text-slate-400 py-3">
                  {t('chat.empty_messages')}
                </div>
              ) : messages.map(m => {
                const mine = (m.sender_email || '').toLowerCase() === myEmail;
                const senderName = m.sender_company
                  || (m.sender_role === 'organizer' ? t('chat.organizer_default') : t('chat.supplier_default'));
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                    data-testid={`chat-msg-${m.id}`}>
                    <div className={`max-w-[80%] sm:max-w-[75%] rounded-lg px-3 py-1.5 ${mine ? 'bg-[#0046AD] text-white' : 'bg-white border border-slate-200 text-slate-800'}`}>
                      {!mine && (
                        <div className="text-[10px] font-semibold opacity-70 mb-0.5">
                          {senderName}
                        </div>
                      )}
                      {m.message_text && (
                        <div className="text-xs sm:text-sm whitespace-pre-wrap break-words">{m.message_text}</div>
                      )}
                      {(m.attachments || []).length > 0 && (
                        <div className="mt-1 space-y-1">
                          {m.attachments.map(a => (
                            <button key={a.id} onClick={() => downloadAttachment(a)}
                              data-testid={`chat-att-${a.id}`}
                              className={`w-full text-left flex items-center gap-2 px-2 py-1 rounded text-[11px] ${mine ? 'bg-white/20 hover:bg-white/30' : 'bg-slate-100 hover:bg-slate-200'}`}>
                              {isImage(a.content_type)
                                ? <ImageIcon className="w-3.5 h-3.5 shrink-0" />
                                : <FileText className="w-3.5 h-3.5 shrink-0" />}
                              <span className="flex-1 truncate">{a.filename}</span>
                              <span className={`text-[10px] ${mine ? 'opacity-70' : 'text-slate-500'}`}>{fmtSize(a.size, lang)}</span>
                              <Download className="w-3 h-3 opacity-70" />
                            </button>
                          ))}
                        </div>
                      )}
                      <div className={`text-[9px] text-right mt-0.5 ${mine ? 'opacity-70' : 'text-slate-400'}`}>
                        {fmtTime(m.timestamp, lang)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            {(() => {
              const lockedStatus = (active.tender_status || '').toLowerCase();
              const isLocked = lockedStatus === 'completed' || lockedStatus === 'cancelled';
              if (isLocked) {
                return (
                  <div className="border-t border-slate-200 bg-amber-50/60 px-3 py-2.5 text-[11px] text-amber-800 flex items-center gap-2"
                    data-testid="chat-locked-banner">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-200 text-amber-800 font-bold">!</span>
                    <span>
                      {lockedStatus === 'cancelled' ? t('chat.locked_cancelled') : t('chat.locked_completed')}
                    </span>
                  </div>
                );
              }
              return (
                <div className="border-t border-slate-200 bg-white p-2">
                  {files.length > 0 && (
                    <div className="mb-1.5 flex flex-wrap gap-1">
                      {files.map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 text-[10px]"
                          data-testid={`chat-file-pending-${i}`}>
                          <Paperclip className="w-2.5 h-2.5" />
                          <span className="max-w-[100px] truncate">{f.name}</span>
                          <span className="text-slate-400">{fmtSize(f.size, lang)}</span>
                          <button onClick={() => setFiles(p => p.filter((_, idx) => idx !== i))}
                            className="text-rose-500 hover:text-rose-700 ml-0.5">
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-end gap-1.5">
                    <input ref={fileInputRef} type="file" multiple className="hidden"
                      onChange={(e) => handleFiles(e.target.files)}
                      data-testid="chat-file-input" />
                    <button onClick={() => fileInputRef.current?.click()}
                      data-testid="chat-attach-btn"
                      className="w-9 h-9 sm:w-8 sm:h-8 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      placeholder={t('chat.placeholder')}
                      rows={1}
                      data-testid="chat-input"
                      className="flex-1 resize-none border border-slate-300 rounded px-2 py-1.5 text-[13px] sm:text-xs focus:outline-none focus:border-[#0046AD] max-h-24 min-h-[36px]"
                    />
                    <Button onClick={handleSend} disabled={sending || (!draft.trim() && files.length === 0)}
                      data-testid="chat-send-btn"
                      size="sm"
                      className="h-9 w-9 sm:h-8 sm:w-auto sm:px-2.5 bg-[#0046AD] hover:bg-[#003388] text-white rounded shrink-0">
                      <Send className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );

  // Общий контент-блок (header + список / диалог). Используется в обоих режимах.
  const renderBody = () => (
    <>
      {/* Header */}
      <div className={`${isMobile ? '' : 'chat-handle cursor-move'} bg-[#0046AD] text-white px-3 py-2 flex items-center justify-between select-none`}
        data-testid="chat-handle">
        <div className="flex items-center gap-2 min-w-0">
          {/* На мобильном — кнопка «назад» когда открыт диалог */}
          {isMobile && active && (
            <button onClick={() => setActiveKey(null)} data-testid="chat-back-mobile"
              className="w-8 h-8 -ml-1 hover:bg-white/20 rounded flex items-center justify-center">
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          <MessageCircle className="w-4 h-4 shrink-0" />
          <span className="text-sm font-semibold truncate">
            {isMobile && active ? peerNameForActive : t('chat.title')}
          </span>
          {totalUnread > 0 && !minimized && (!isMobile || !active) && (
            <Badge className="bg-rose-500 hover:bg-rose-500 text-white text-[10px] h-5 rounded-full px-1.5 shrink-0">
              {totalUnread}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isMobile && (
            <button onClick={() => setMinimized(m => !m)} data-testid="chat-minimize"
              className="w-7 h-7 hover:bg-white/20 rounded flex items-center justify-center">
              <Minus className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={closeWidget} data-testid="chat-close"
            className="w-9 h-9 sm:w-7 sm:h-7 hover:bg-white/20 rounded flex items-center justify-center">
            <X className="w-5 h-5 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
      {!minimized && renderInner()}
    </>
  );

  if (isMobile) {
    // Полноэкранный модальный чат для телефонов.
    return (
      <div
        ref={dragNodeRef}
        data-testid="chat-widget"
        className="fixed inset-0 z-[9999] bg-white flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {renderBody()}
      </div>
    );
  }

  return (
    <Draggable handle=".chat-handle" nodeRef={dragNodeRef} bounds="body" defaultPosition={{ x: -32, y: -32 }} positionOffset={{ x: 'calc(100vw - 760px)', y: 'calc(100vh - 580px)' }}>
      <div ref={dragNodeRef}
        data-testid="chat-widget"
        className="fixed shadow-2xl border border-slate-300 rounded-md bg-white overflow-hidden flex flex-col"
        style={{ width: 720, height: minimized ? 44 : 540, zIndex: 9999, top: 0, left: 0 }}>
        {renderBody()}
      </div>
    </Draggable>
  );
}
